import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Receipt, Download, Check, ArrowUpDown } from 'lucide-react';
import { fmtUSD, AP_STATUS_STYLES, todayISO } from './utils';
import { API_BASE } from '../../utils/apiBase';
import ReceiptScanner from './ReceiptScanner';
import logger from '../../utils/logger';

const EMPTY = { vendor_name: '', vendor_id: '', category: 'Other', bill_date: todayISO(), due_date: '', amount: '', description: '', reference: '', notes: '' };

export default function APTab({ api, onRefresh }) {
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [config, setConfig] = useState({ ap_categories: [] });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [vendorDialog, setVendorDialog] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: '', contact_name: '', email: '', phone: '', category: '', notes: '' });
  const [filter, setFilter] = useState('all');
  const [sortKey, setSortKey] = useState('smart');  // smart | due_asc | due_desc | amount_desc | amount_asc | vendor

  const STATUS_PRIORITY = { overdue: 0, pending: 1, paid: 2 };
  const HIGH = '9999-12-31';
  const cmpDate = (a, b) => (a || HIGH).localeCompare(b || HIGH);

  const sortedBills = React.useMemo(() => {
    const arr = [...bills];
    if (sortKey === 'smart') {
      // Same workflow ordering as backend, applied client-side after the
      // status filter so the user-facing list is always consistent.
      arr.sort((a, b) => {
        const pa = STATUS_PRIORITY[a.status] ?? 3;
        const pb = STATUS_PRIORITY[b.status] ?? 3;
        if (pa !== pb) return pa - pb;
        if (a.status === 'paid') {
          // Most recently paid first
          return (b.payment_date || '').localeCompare(a.payment_date || '');
        }
        // overdue / pending: soonest due first
        const d = cmpDate(a.due_date, b.due_date);
        if (d !== 0) return d;
        return (a.vendor_name || '').localeCompare(b.vendor_name || '');
      });
    } else if (sortKey === 'due_asc') {
      arr.sort((a, b) => cmpDate(a.due_date, b.due_date));
    } else if (sortKey === 'due_desc') {
      arr.sort((a, b) => cmpDate(b.due_date, a.due_date));
    } else if (sortKey === 'amount_desc') {
      arr.sort((a, b) => (b.amount || 0) - (a.amount || 0));
    } else if (sortKey === 'amount_asc') {
      arr.sort((a, b) => (a.amount || 0) - (b.amount || 0));
    } else if (sortKey === 'vendor') {
      arr.sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''));
    }
    return arr;
  }, [bills, sortKey]);

  const filtered = sortedBills.filter(b => filter === 'all' || b.status === filter);

  const fetchAll = useCallback(async () => {
    try {
      const [b, v, c] = await Promise.all([api.get('/finance/ap'), api.get('/finance/vendors'), api.get('/finance/config')]);
      setBills(b.data); setVendors(v.data); setConfig(c.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      vendor_name: b.vendor_name || '', vendor_id: b.vendor_id || '',
      category: b.category || 'Other', bill_date: b.bill_date || todayISO(),
      due_date: b.due_date || '', amount: b.amount || '',
      description: b.description || '', reference: b.reference || '', notes: b.notes || '',
      payment_date: b.payment_date || '', payment_method: b.payment_method || '',
    });
    setDialog(true);
  };

  const save = async () => {
    try {
      if (!form.vendor_name && !form.vendor_id) { toast.error('Vendor required'); return; }
      if (!form.amount) { toast.error('Amount required'); return; }
      if (editing) { await api.put(`/finance/ap/${editing.id}`, form); toast.success('Bill updated'); }
      else { await api.post('/finance/ap', form); toast.success('Bill added'); }
      setDialog(false); fetchAll(); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const markPaid = async (b) => {
    try {
      await api.put(`/finance/ap/${b.id}`, { payment_date: todayISO() });
      toast.success('Marked paid');
      fetchAll(); onRefresh?.();
    } catch { toast.error('Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this bill?')) return;
    await api.delete(`/finance/ap/${id}`); fetchAll(); onRefresh?.();
  };

  const saveVendor = async () => {
    try {
      if (!vendorForm.name.trim()) { toast.error('Name required'); return; }
      const { data } = await api.post('/finance/vendors', vendorForm);
      toast.success('Vendor added');
      setVendorDialog(false); setVendorForm({ name: '', contact_name: '', email: '', phone: '', category: '', notes: '' });
      setVendors(v => [...v, data]);
      setForm(f => ({ ...f, vendor_id: data.id, vendor_name: data.name }));
    } catch { toast.error('Failed'); }
  };

  const totals = {
    pending: bills.filter(b => b.status === 'pending').reduce((s, b) => s + (b.amount || 0), 0),
    overdue: bills.filter(b => b.status === 'overdue').reduce((s, b) => s + (b.amount || 0), 0),
    paid_month: bills.filter(b => b.status === 'paid' && (b.payment_date || '') >= todayISO().slice(0, 7)).reduce((s, b) => s + (b.amount || 0), 0),
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-3">
          <Card className="border-[#FFB74D] bg-[#FFF3E0]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#E65100] font-semibold">Pending</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.pending)}</p></CardContent></Card>
          <Card className="border-red-300 bg-red-50"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#D9534F] font-semibold">Overdue</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.overdue)}</p></CardContent></Card>
          <Card className="border-[#81C784] bg-[#E8F5E9]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#1B5E20] font-semibold">Paid (MTD)</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.paid_month)}</p></CardContent></Card>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/api/finance/export/ap`, '_blank')} className="border-[#DED8EB] gap-1 h-9" data-testid="ap-export-btn"><Download className="h-3.5 w-3.5" /> CSV</Button>
          <Button variant="outline" onClick={() => setVendorDialog(true)} className="border-[#DED8EB] gap-1 h-9" data-testid="ap-vendor-btn">+ Vendor</Button>
          <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-9" data-testid="ap-add-btn"><Plus className="h-4 w-4" /> Add Bill</Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {[['all','All'],['pending','Pending'],['overdue','Overdue'],['paid','Paid']].map(([k, l]) => (
          <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} onClick={() => setFilter(k)}
            className={filter === k ? 'bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-8' : 'border-[#DED8EB] h-8'}
            data-testid={`ap-filter-${k}`}>{l}</Button>
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          <ArrowUpDown className="h-3.5 w-3.5 text-[#8E85A0]" />
          <select value={sortKey} onChange={e => setSortKey(e.target.value)}
            className="border border-[#DED8EB] rounded-md px-2 py-1 text-xs bg-white h-8" data-testid="ap-sort">
            <option value="smart">Smart (Overdue → Pending → Paid)</option>
            <option value="due_asc">Due date · soonest first</option>
            <option value="due_desc">Due date · latest first</option>
            <option value="amount_desc">Amount · highest first</option>
            <option value="amount_asc">Amount · lowest first</option>
            <option value="vendor">Vendor name (A–Z)</option>
          </select>
        </div>
      </div>

      {loading ? <p className="text-sm text-[#8E85A0]">Loading…</p> : filtered.length === 0 ? (
        <div className="text-center py-10 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg">
          <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No bills {filter !== 'all' && `(${filter})`}. Add one to get started.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="ap-list">
          {filtered.map((b, idx) => {
            const showHeader = sortKey === 'smart' && filter === 'all' && (idx === 0 || filtered[idx - 1].status !== b.status);
            const headerMeta = {
              overdue: { label: 'Overdue', color: '#D9534F', bg: '#FDECEA' },
              pending: { label: 'Pending', color: '#E65100', bg: '#FFF3E0' },
              paid:    { label: 'Paid',    color: '#1B5E20', bg: '#E8F5E9' },
            }[b.status] || { label: b.status, color: '#5A4D6E', bg: '#F8F7FC' };
            return (
            <React.Fragment key={b.id}>
              {showHeader && (
                <div className="flex items-center gap-2 pt-1 pb-0.5" data-testid={`ap-section-${b.status}`}>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ background: headerMeta.bg, color: headerMeta.color }}>
                    {headerMeta.label}
                  </span>
                  <span className="text-[10px] text-[#8E85A0]">
                    {filtered.filter(x => x.status === b.status).length} bill{filtered.filter(x => x.status === b.status).length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex-1 h-px bg-[#F0EDF6]" />
                </div>
              )}
            <Card className={`border shadow-sm bg-white ${AP_STATUS_STYLES[b.status]?.includes('red') ? 'border-l-4 border-l-red-400' : ''}`} data-testid={`ap-row-${b.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-[#2D1B4E]">{b.vendor_name}</h3>
                      <Badge className={`text-[10px] border ${AP_STATUS_STYLES[b.status]}`}>{b.status}</Badge>
                      <span className="text-[10px] text-[#8E85A0]">{b.category}</span>
                    </div>
                    <p className="text-xs text-[#5A4D6E] mt-0.5">
                      Due {b.due_date || '—'} · Billed {b.bill_date} {b.reference ? `· #${b.reference}` : ''}
                    </p>
                    {b.description && <p className="text-[11px] text-[#8E85A0] mt-0.5">{b.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(b.amount)}</p>
                    {b.status !== 'paid' && (
                      <Button size="icon" variant="ghost" onClick={() => markPaid(b)} title="Mark paid" className="h-7 w-7 text-[#1B5E20] hover:bg-[#E8F5E9]" data-testid={`ap-pay-${b.id}`}><Check className="h-4 w-4" /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(b)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`ap-edit-${b.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(b.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`ap-del-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            </React.Fragment>
          );})}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="ap-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">{editing ? 'Edit Bill' : 'New Bill'}</DialogTitle></DialogHeader>
          {!editing && (
            <div className="pb-2 border-b border-[#F0EDF6]">
              <ReceiptScanner api={api} kind="ap" onExtracted={(f) => {
                setForm(prev => ({
                  ...prev,
                  vendor_name: f.vendor_name || prev.vendor_name,
                  amount: f.amount ?? prev.amount,
                  bill_date: f.bill_date || prev.bill_date,
                  due_date: f.due_date || prev.due_date,
                  category: f.category || prev.category,
                  description: f.description || prev.description,
                  reference: f.reference || prev.reference,
                  vendor_id: '',
                }));
              }} />
            </div>
          )}
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Vendor</Label>
              <Select value={form.vendor_id || form.vendor_name} onValueChange={v => {
                const vn = vendors.find(x => x.id === v);
                setForm(f => ({ ...f, vendor_id: vn ? vn.id : '', vendor_name: vn ? vn.name : v }));
              }}>
                <SelectTrigger className="border-[#DED8EB]" data-testid="ap-vendor-select"><SelectValue placeholder="Select or type…" /></SelectTrigger>
                <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Or type a vendor name" value={form.vendor_name} onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value, vendor_id: '' }))} className="border-[#DED8EB] mt-1" data-testid="ap-vendor-text" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bill Date</Label><Input type="date" value={form.bill_date} onChange={e => setForm(f => ({ ...f, bill_date: e.target.value }))} className="border-[#DED8EB]" data-testid="ap-bill-date" /></div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="border-[#DED8EB]" data-testid="ap-due-date" /></div>
              <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="border-[#DED8EB]" data-testid="ap-amount" /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="border-[#DED8EB]" data-testid="ap-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{(config.ap_categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Reference / Invoice #</Label><Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} className="border-[#DED8EB]" data-testid="ap-reference" /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="border-[#DED8EB]" data-testid="ap-desc" /></div>
            </div>
            {editing && (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#F0EDF6]">
                <div><Label>Payment Date</Label><Input type="date" value={form.payment_date || ''} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} className="border-[#DED8EB]" data-testid="ap-pay-date" /></div>
                <div><Label>Payment Method</Label><Input value={form.payment_method || ''} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))} placeholder="Check/ACH/Card" className="border-[#DED8EB]" data-testid="ap-pay-method" /></div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="ap-save-btn">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={vendorDialog} onOpenChange={setVendorDialog}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="vendor-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">New Vendor</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name *</Label><Input value={vendorForm.name} onChange={e => setVendorForm(v => ({ ...v, name: e.target.value }))} className="border-[#DED8EB]" data-testid="vendor-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact</Label><Input value={vendorForm.contact_name} onChange={e => setVendorForm(v => ({ ...v, contact_name: e.target.value }))} className="border-[#DED8EB]" data-testid="vendor-contact" /></div>
              <div><Label>Category</Label>
                <Select value={vendorForm.category} onValueChange={v => setVendorForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="border-[#DED8EB]" data-testid="vendor-cat"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>{(config.ap_categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Email</Label><Input value={vendorForm.email} onChange={e => setVendorForm(v => ({ ...v, email: e.target.value }))} className="border-[#DED8EB]" data-testid="vendor-email" /></div>
              <div><Label>Phone</Label><Input value={vendorForm.phone} onChange={e => setVendorForm(v => ({ ...v, phone: e.target.value }))} className="border-[#DED8EB]" data-testid="vendor-phone" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setVendorDialog(false)}>Cancel</Button><Button onClick={saveVendor} className="bg-[#6B3FA0] text-white" data-testid="vendor-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
